import { useApolloClient } from "@apollo/client";
import { MenuItem } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import React from "react";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useSubStatusTransition } from "src/hooks/useSubStatusTransition";
import { asyncForEach } from "src/services/asyncHelpers";
import { maybePluralize } from "src/services/format";
import * as GQL from "src/types/graphql";
import { FormSchoolGradeNotFoundError, OfferError } from "../errors";
import {
  FETCH_OFFER_WAITLIST_STATUS,
  GET_ASSIGNED_FORMS_BY_FORM_ID,
  GET_FORM_VIEW_BY_ID,
} from "../graphql/queries";
import type { FormSchoolStatus, SchoolFormId } from "../types";
import { canMakeOffers } from "../validations/makeOffers";
import { trackFormAction, trackFormActionError } from "./trackFormAction";
import {
  ConfirmationResult,
  useMakeOfferConfirmationDialog,
} from "./useMakeOfferConfirmationDialog";

const MAKE_BULK_FORM_SCHOOL_OFFER_MAX_INPUT_SIZE = 500;

interface Props {
  selectedFormSchoolStatuses: FormSchoolStatus[];
  selectedFormSchools: SchoolFormId[];
  onRefetch?: () => void;
  menuLabel?: string;
  isBulkAction?: boolean;
}

export const MakeOffer = ({
  selectedFormSchoolStatuses,
  selectedFormSchools,
  onRefetch,
  menuLabel = "Make offers",
  isBulkAction = false,
}: Props) => {
  const client = useApolloClient();
  const queryClient = useQueryClient();

  const { mutateAsync: makeBulkFormSchoolOffer } = FSQ.useBulkMakeOffers();
  const { isSubStatusTransitionAllowed } = useSubStatusTransition();

  const toast = useAvelaToast();

  const { confirm, confirmationDialog } = useMakeOfferConfirmationDialog(
    selectedFormSchoolStatuses
  );

  const handleOnClick = React.useCallback(async () => {
    const schoolForms = selectedFormSchoolStatuses;
    const count = schoolForms.length;
    const trackArgs = {
      actionType: "MAKE_OFFER" as const,
      bulk: isBulkAction,
      count,
      async: false,
    };

    trackFormAction({ verb: "Clicked", ...trackArgs });

    // programID may be a program ID or null when a grade does not have programs
    // programID is undefined when the makeOffers action is cancelled
    const confirmationResult: ConfirmationResult = await confirm(schoolForms);

    if (confirmationResult.result === "Cancelled") {
      trackFormAction({ verb: "Canceled", ...trackArgs });
      return;
    }

    trackFormAction({ verb: "Confirmed", ...trackArgs });

    const offerNoun = maybePluralize(count, "offer");
    const loadingToastId = toast({
      title: `Working hard`,
      description: `Making ${count} ${offerNoun}...`,
      status: "loading",
      duration: null,
    });

    const programID: string | null = confirmationResult.programId;

    try {
      await asyncForEach(
        // Chunk the request to avoid API Gateway timeout
        _.chunk(schoolForms, MAKE_BULK_FORM_SCHOOL_OFFER_MAX_INPUT_SIZE),
        async (formSchoolChunk) => {
          await makeBulkFormSchoolOffer({
            body: formSchoolChunk.map((result) => ({
              form_id: result.form_id,
              school_id: result.school_id,
              program_id: programID ?? undefined,
            })),
          });
        }
      );

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [FSQ.useGetCapacitiesBySchoolAndEnrollmentPeriodKey],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [FSQ.useGetCapacitySummaryByEnrollmentPeriodKey],
          exact: false,
        }),
        client.refetchQueries({
          include: [
            FETCH_OFFER_WAITLIST_STATUS,
            GET_FORM_VIEW_BY_ID,
            GET_ASSIGNED_FORMS_BY_FORM_ID,
          ],
        }),
      ]);

      if (onRefetch) onRefetch();

      toast.close(loadingToastId);
      toast({
        title: "Hooray",
        description: `${count} ${offerNoun} made`,
        status: "success",
      });
      trackFormAction({ verb: "Completed", ...trackArgs });
    } catch (err: any) {
      trackFormActionError(err, trackArgs);
      toast.close(loadingToastId);
      let message = "Check your network and try again.";

      if (
        err instanceof FormSchoolGradeNotFoundError ||
        err instanceof OfferError
      ) {
        message = err.message;
      }

      toast.error({
        title: "Oops! Something went wrong!",
        description: message,
      });
      console.error(err);
    }
  }, [
    client,
    confirm,
    isBulkAction,
    makeBulkFormSchoolOffer,
    onRefetch,
    queryClient,
    selectedFormSchoolStatuses,
    toast,
  ]);
  return (
    <>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={handleOnClick}
        isDisabled={
          !canMakeOffers(
            selectedFormSchoolStatuses,
            selectedFormSchools,
            (subStatus) =>
              isSubStatusTransitionAllowed(
                subStatus,
                GQL.offer_status_enum.Offered
              )
          )
        }
      >
        {menuLabel}
      </MenuItem>
      {confirmationDialog}
    </>
  );
};
